(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const initReveal = (root = document) => {
    const nodes = root.querySelectorAll('[data-moso-reveal]:not([data-moso-reveal-ready])');
    if (!nodes.length) return;

    if (reducedMotion || !('IntersectionObserver' in window)) {
      nodes.forEach((node) => {
        node.dataset.mosoRevealReady = 'true';
        node.classList.add('is-revealed');
      });
      return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        obs.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    nodes.forEach((node) => {
      node.dataset.mosoRevealReady = 'true';
      observer.observe(node);
    });
  };

  const initMosoPurchaseDock = (root = document) => {
    root.querySelectorAll('[data-moso-purchase-dock]').forEach((dock) => {
      if (dock.dataset.mosoDockReady === 'true') return;
      dock.dataset.mosoDockReady = 'true';

      const sectionId = dock.dataset.sectionId;
      const qtyValue = dock.querySelector('[data-moso-dock-qty]');
      const minusButton = dock.querySelector('[data-moso-dock-minus]');
      const plusButton = dock.querySelector('[data-moso-dock-plus]');
      const submitButton = dock.querySelector('[data-moso-dock-submit]');
      const submitLabel = dock.querySelector('[data-moso-dock-label]');
      const total = dock.querySelector('[data-moso-dock-total]');
      const productInfo = document.querySelector(`#ProductInfo-${sectionId}`);
      const variantDataNode = dock.querySelector('[data-moso-dock-variants]');
      let variantRecords = [];
      try {
        variantRecords = variantDataNode ? JSON.parse(variantDataNode.textContent || '[]') : [];
      } catch (_) {
        variantRecords = [];
      }

      if (!qtyValue || !minusButton || !plusButton || !submitButton || !submitLabel || !total) return;

      let unitPrice = Number.parseInt(dock.dataset.unitPrice || '0', 10) || 0;
      let quantity = Number.parseInt(dock.dataset.quantity || '1', 10) || 1;
      let min = Number.parseInt(dock.dataset.min || '1', 10) || 1;
      let step = Number.parseInt(dock.dataset.step || '1', 10) || 1;
      let max = dock.dataset.max ? Number.parseInt(dock.dataset.max, 10) : null;
      let available = dock.dataset.available === 'true';
      let busy = false;

      const currency = dock.dataset.currency || 'GBP';
      const locale = document.documentElement.lang || 'en-GB';
      let moneyFormatter;
      try {
        moneyFormatter = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      } catch (_) {
        moneyFormatter = new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: 'GBP',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }

      const getSourceButton = () => document.querySelector(`#ProductSubmitButton-${sectionId}`);
      const getSourceQuantity = () => document.querySelector(`#product-form-${sectionId} [data-moso-source-quantity]`);

      const applyVariantState = (variant) => {
        if (!variant) return;
        const record = variantRecords.find((item) => String(item.id) === String(variant.id)) || {};
        const rule = variant.quantity_rule || {};

        unitPrice = Number.parseInt(record.price ?? variant.price ?? unitPrice, 10) || 0;
        available = Boolean(record.available ?? variant.available);
        min = Number.parseInt(record.min ?? rule.min ?? 1, 10) || 1;
        step = Number.parseInt(record.increment ?? rule.increment ?? 1, 10) || 1;

        const ruleMaxRaw = record.max ?? rule.max;
        const ruleMax = ruleMaxRaw === null || ruleMaxRaw === undefined ? null : Number.parseInt(ruleMaxRaw, 10);
        let inventoryMax = null;
        if (record.inventory_management === 'shopify' && record.inventory_policy !== 'continue') {
          inventoryMax = Math.max(Number.parseInt(record.inventory_quantity ?? 0, 10) || 0, 0);
        }

        if (ruleMax !== null && Number.isFinite(ruleMax) && inventoryMax !== null) {
          max = Math.min(ruleMax, inventoryMax);
        } else if (ruleMax !== null && Number.isFinite(ruleMax)) {
          max = ruleMax;
        } else {
          max = inventoryMax;
        }

        if (max !== null && max < min) available = false;
      };

      const normaliseQuantity = (value) => {
        let next = Number.isFinite(value) ? value : min;
        next = Math.max(min, next);
        if (max !== null && Number.isFinite(max)) next = Math.min(max, next);
        const offset = next - min;
        next = min + Math.round(offset / step) * step;
        if (max !== null && Number.isFinite(max)) next = Math.min(max, next);
        return Math.max(min, next);
      };

      const syncSourceQuantity = () => {
        const sourceQuantity = getSourceQuantity();
        if (sourceQuantity) sourceQuantity.value = String(quantity);
      };

      const render = () => {
        quantity = normaliseQuantity(quantity);
        qtyValue.textContent = String(quantity);
        total.textContent = moneyFormatter.format((unitPrice * quantity) / 100);

        minusButton.disabled = !available || busy || quantity <= min;
        plusButton.disabled = !available || busy || (max !== null && quantity >= max);
        submitButton.disabled = !available || busy;

        if (!available) {
          submitLabel.textContent = 'Out of stock';
        } else if (busy) {
          submitLabel.textContent = 'Adding…';
        } else if (quantity > 1) {
          submitLabel.textContent = `(${quantity}) Add to basket`;
        } else {
          submitLabel.textContent = 'Add to basket';
        }

        syncSourceQuantity();
      };

      const syncStateFromSource = () => {
        const sourceButton = getSourceButton();
        if (!sourceButton) return;
        available = !sourceButton.disabled;
        busy = sourceButton.getAttribute('aria-disabled') === 'true' || sourceButton.classList.contains('loading');
        render();
      };

      minusButton.addEventListener('click', () => {
        if (!available) return;
        quantity = normaliseQuantity(quantity - step);
        render();
      });

      plusButton.addEventListener('click', () => {
        if (!available) return;
        quantity = normaliseQuantity(quantity + step);
        render();
      });

      submitButton.addEventListener('click', () => {
        const sourceButton = getSourceButton();
        syncSourceQuantity();
        if (sourceButton && !sourceButton.disabled && sourceButton.getAttribute('aria-disabled') !== 'true') {
          busy = true;
          render();
          sourceButton.click();
        }
      });

      if (productInfo) {
        const observer = new MutationObserver(() => {
          window.requestAnimationFrame(syncStateFromSource);
        });
        observer.observe(productInfo, { attributes: true, childList: true, subtree: true });
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event?.data || event.data.sectionId !== sectionId || !event.data.variant) return;
          const variant = event.data.variant;
          busy = false;
          applyVariantState(variant);
          quantity = min;
          render();
        });
      }

      const initialVariantId = dock.dataset.variantId;
      const initialVariant = variantRecords.find((item) => String(item.id) === String(initialVariantId));
      if (initialVariant) applyVariantState(initialVariant);

      render();
      syncStateFromSource();
    });
  };


  const initDesktopPurchaseRail = (root = document) => {
    root.querySelectorAll('[data-moso-purchase-dock]').forEach((dock) => {
      if (dock.dataset.mosoDesktopRailReady === 'true') return;
      dock.dataset.mosoDesktopRailReady = 'true';

      const sectionId = dock.dataset.sectionId;
      const desktopQuery = window.matchMedia('(min-width: 990px)');
      const headerSection = document.querySelector('.section-header');
      let rafId = null;

      const getSourceButton = () => document.querySelector(`#ProductSubmitButton-${sectionId}`);

      const update = () => {
        rafId = null;

        if (!desktopQuery.matches) {
          dock.classList.remove('moso-purchase-dock--desktop-visible');
          dock.style.removeProperty('--moso-desktop-dock-top');
          return;
        }

        const sourceButton = getSourceButton();
        if (!sourceButton) {
          dock.classList.remove('moso-purchase-dock--desktop-visible');
          return;
        }

        const sourceRect = sourceButton.getBoundingClientRect();
        const shouldShow = sourceRect.bottom <= 0 && window.scrollY > 0;
        dock.classList.toggle('moso-purchase-dock--desktop-visible', shouldShow);

        let top = 0;
        if (shouldShow && headerSection) {
          const headerHidden = headerSection.classList.contains('shopify-section-header-hidden');
          const headerSticky = headerSection.classList.contains('shopify-section-header-sticky');

          if (headerSticky && !headerHidden) {
            const headerRect = headerSection.getBoundingClientRect();
            top = Math.max(0, Math.round(headerRect.bottom));
          }
        }

        dock.style.setProperty('--moso-desktop-dock-top', `${top}px`);
      };

      const requestUpdate = () => {
        if (rafId !== null) return;
        rafId = window.requestAnimationFrame(update);
      };

      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate, { passive: true });

      if (typeof desktopQuery.addEventListener === 'function') {
        desktopQuery.addEventListener('change', requestUpdate);
      } else if (typeof desktopQuery.addListener === 'function') {
        desktopQuery.addListener(requestUpdate);
      }

      if (headerSection) {
        const headerObserver = new MutationObserver(requestUpdate);
        headerObserver.observe(headerSection, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      update();
    });
  };

  const initFaqAccordions = (root = document) => {
    root.querySelectorAll('.moso-faqs__list').forEach((list) => {
      if (list.dataset.mosoFaqReady === 'true') return;
      list.dataset.mosoFaqReady = 'true';

      list.querySelectorAll('details.moso-faq').forEach((item) => {
        item.addEventListener('toggle', () => {
          if (!item.open) return;
          list.querySelectorAll('details.moso-faq[open]').forEach((other) => {
            if (other !== item) other.removeAttribute('open');
          });
        });
      });
    });
  };



  const initKeyElementSliders = (root = document) => {
    root.querySelectorAll('[data-moso-key-slider]').forEach((slider) => {
      if (slider.dataset.mosoKeyReady === 'true') return;
      slider.dataset.mosoKeyReady = 'true';

      const track = slider.querySelector('[data-moso-key-track]');
      const thumb = slider.querySelector('[data-moso-key-progress]');
      if (!track || !thumb) return;

      const slides = Array.from(track.children);
      const count = Math.max(slides.length, 1);
      let ticking = false;

      const update = () => {
        ticking = false;
        const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
        const ratio = maxScroll > 0 ? Math.min(Math.max(track.scrollLeft / maxScroll, 0), 1) : 0;
        const trackWidth = thumb.parentElement?.clientWidth || 0;
        const thumbWidth = trackWidth / count;
        const travel = Math.max(trackWidth - thumbWidth, 0);

        thumb.style.width = `${100 / count}%`;
        thumb.style.transform = `translate3d(${ratio * travel}px,0,0)`;
      };

      const requestUpdate = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      };

      track.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate, { passive: true });
      update();
    });
  };


  const initProductGalleryProgress = (root = document) => {
    root.querySelectorAll('[data-moso-gallery-progress]').forEach((progress) => {
      if (progress.dataset.mosoGalleryReady === 'true') return;
      progress.dataset.mosoGalleryReady = 'true';

      const sliderId = progress.dataset.gallerySliderId;
      const track = sliderId ? document.getElementById(sliderId) : null;
      const thumb = progress.querySelector('[data-moso-gallery-progress-thumb]');
      if (!track || !thumb) return;

      let ticking = false;

      const getVisibleSlides = () =>
        Array.from(track.children).filter((slide) => slide.getBoundingClientRect().width > 0);

      const update = () => {
        ticking = false;
        const slides = getVisibleSlides();
        const count = Math.max(slides.length, 1);
        const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
        const ratio = maxScroll > 0 ? Math.min(Math.max(track.scrollLeft / maxScroll, 0), 1) : 0;
        const railWidth = progress.clientWidth || 0;
        const thumbWidth = railWidth / count;
        const travel = Math.max(railWidth - thumbWidth, 0);

        thumb.style.width = `${100 / count}%`;
        thumb.style.transform = `translate3d(${ratio * travel}px,0,0)`;
      };

      const requestUpdate = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      };

      track.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate, { passive: true });

      const mutationObserver = new MutationObserver(requestUpdate);
      mutationObserver.observe(track, { attributes: true, childList: true, subtree: true });

      update();
    });
  };


  const initAll = (root = document) => {
    initReveal(root);
    initMosoPurchaseDock(root);
    initDesktopPurchaseRail(root);
    initFaqAccordions(root);
    initKeyElementSliders(root);
    initProductGalleryProgress(root);
  };

  document.addEventListener('DOMContentLoaded', () => initAll(document));
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
